import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#syncEveryLegendHeaderOverlays();
    window.addEventListener('resize', this.#syncEveryLegendHeaderOverlays, { passive: true });
    window.addEventListener('scroll', this.#syncEveryLegendHeaderOverlays, { passive: true });
    document.addEventListener('click', this.#syncEveryLegendHeaderOverlays, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    window.removeEventListener('resize', this.#syncEveryLegendHeaderOverlays);
    window.removeEventListener('scroll', this.#syncEveryLegendHeaderOverlays);
    document.removeEventListener('click', this.#syncEveryLegendHeaderOverlays, true);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  #syncEveryLegendHeaderOverlays = () => {
    requestAnimationFrame(() => {
      const nav = document.getElementById('everylegend-sticky-shop-nav');
      const navRect = nav?.getBoundingClientRect();
      const header =
        document.querySelector('#header-component .header__row') ||
        document.querySelector('#header-component .header__columns') ||
        document.querySelector('#header-component');
      const headerRect = header?.getBoundingClientRect();
      const top =
        navRect && navRect.bottom > 0
          ? Math.max(0, Math.round(navRect.bottom) - 1)
          : headerRect
            ? Math.max(0, Math.round(headerRect.bottom) - 1)
            : 0;
      document.documentElement.style.setProperty('--everylegend-cart-drawer-top', '0px');

      const cartDialog = document.querySelector('#cart-drawer .theme-drawer__dialog');
      if (cartDialog instanceof HTMLElement) {
        cartDialog.style.setProperty('top', '0', 'important');
        cartDialog.style.setProperty('height', '100dvh', 'important');
        cartDialog.style.setProperty('border-top', '0', 'important');
      }

      const localizationWrapper = document.querySelector('dropdown-localization-component .localization-wrapper');
      if (localizationWrapper instanceof HTMLElement) {
        localizationWrapper.style.setProperty('position', 'absolute', 'important');
        localizationWrapper.style.setProperty('top', 'calc(100% + 10px)', 'important');
        localizationWrapper.style.setProperty('right', '0', 'important');
        localizationWrapper.style.setProperty('left', 'auto', 'important');
        localizationWrapper.style.setProperty('z-index', '1600', 'important');
        localizationWrapper.style.setProperty('width', 'min(390px, calc(100vw - 24px))', 'important');
        localizationWrapper.style.setProperty('max-height', 'min(27.5rem, calc(100vh - 132px))', 'important');
        localizationWrapper.style.setProperty('overflow', 'auto', 'important');
        localizationWrapper.style.setProperty('transform', 'none', 'important');
      }
    });
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
